import {
  Box,
  Checkbox,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import styled from 'styled-components';
import React, { FC, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import Accordion from '../Accordion/Accordion';
import { Star } from '@mui/icons-material';
import KeywordDetail from '../KeywordDetail/KeywordDetail';
import Helper from '../../shared/helpers/Helper';
import KeywordService from '../../services/KeywordService';
import { IKeywordSearchResultResponse } from '../../shared/interfaces/IKeywordSearchResultResponse';
import KeywordMonitoringService from '../../services/KeywordMonitoringService';
import Chip from '../Chip/Chip';
import { useTranslation } from 'react-i18next';
import Confirm from '../Confirm/Confirm';

interface Props {
  monitoring_id: string;
  platform: string;
  search: string;
  searchHash: string;
  country: string;
  date: string;
  onDelete: (id: string) => void;
  url?: string;
}

const AccordionSummaryContentBox = styled(Box)`
  display: flex;
  align-items: center;
`;

const StyledImages = styled.img`
  height: 32px;
`;

const Keyword: FC<Props> = ({
  monitoring_id,
  platform,
  search,
  searchHash,
  country,
  date,
  onDelete,
  url,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [isExpanded, setIsExpanded] = useState(false);
  const [deleteIsOpen, setDeleteIsOpen] = useState(false);
  const [requestedDataOnce, setRequestedDataOnce] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<IKeywordSearchResultResponse[]>([]);

  const deleteMonitoredKeyword = async () => {
    const response = await KeywordMonitoringService.deleteMonitoredKeyword(monitoring_id);
    if (!('success' in response)) return;
    const { success } = response;
    if (!success) return;
    onDelete(monitoring_id);
  };

  const normalizedCountry =
    {
      de: 'de',
      uk: 'gb',
      at: 'at',
      ch: 'ch',
      nl: 'nl',
      us: 'us',
    }[country ? country.toLowerCase() : 'de'] ?? 'de';

  const normalizedTranslatedCountry =
    {
      de: t('country_germany'),
      uk: t('country_united_kingdom'),
      at: t('country_austria'),
      ch: t('country_switzerland'),
      nl: t('country_netherlands'),
      us: t('country_us'),
    }[country ? country.toLowerCase() : 'de'] ?? 'de';

  const requestDataFromService = () => {
    const controller = new AbortController();

    const getAllSearchResults = async () => {
      const response = await KeywordService.getAllSearchResults(
        searchHash,
        setLoading,
        controller.signal
      );

      if (!Array.isArray(response)) return;
      if (!response) return;

      setSearchResults(response);
    };

    (async () => getAllSearchResults())();

    return () => {
      controller.abort();
    };
  };

  const toggleAccordion = () => {
    setIsExpanded((prevState) => !prevState);
    if (requestedDataOnce === false) {
      setRequestedDataOnce(true);
      requestDataFromService();
    }
  };

  function getLabel() {
    if (url) {
      return `${search} (${url})`;
    }
    return search;
  }

  return (
    <Accordion
      expanded={isExpanded}
      onExpandRequest={() => toggleAccordion()}
      summary={
        <>
          <AccordionSummaryContentBox>
            <Checkbox
              disabled
              sx={{ display: 'none' }}
            />
            <Checkbox
              disabled
              checkedIcon={<Star sx={{ color: theme.palette.secondary.main }} />}
              icon={<Star sx={{ color: theme.palette.text.disabled }} />}
              sx={{ display: 'none', ml: theme.spacing(1) }}
            />
            <StyledImages src={require(`../../assets/images/logos/${platform}.png`)} />
            <Box sx={{ display: 'flex', alignItems: 'center', ml: theme.spacing(2) }}>
              <Tooltip title={normalizedTranslatedCountry}>
                <img
                  src={`https://flagcdn.com/16x12/${normalizedCountry.toLowerCase()}.png`}
                  srcSet={`https://flagcdn.com/32x24/${normalizedCountry.toLowerCase()}.png 2x, 
                https://flagcdn.com/48x36/${normalizedCountry.toLowerCase()}.png 3x`}
                  width="16"
                  height="12"
                  referrerPolicy="no-referrer"
                  alt={normalizedCountry.toLowerCase()}
                />
              </Tooltip>
            </Box>
            <Chip
              size="small"
              label={getLabel()}
              sx={{ ml: theme.spacing(2) }}
            />
          </AccordionSummaryContentBox>
          <AccordionSummaryContentBox>
            <Tooltip
              title={Helper.getFormattedDate(date, false)}
              arrow
            >
              <Typography
                variant="body2"
                sx={{ mr: theme.spacing(1), cursor: 'pointer' }}
              >
                {Helper.getFormattedDate(date, true)}
              </Typography>
            </Tooltip>
            <IconButton
              color="error"
              size="small"
              onClick={() => setDeleteIsOpen(true)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            <Confirm
              text={t('delete_confirm')}
              open={deleteIsOpen}
              onCancel={() => setDeleteIsOpen(false)}
              onOk={async () => await deleteMonitoredKeyword()}
            />
          </AccordionSummaryContentBox>
        </>
      }
      details={
        <>
          {isExpanded && loading && <LinearProgress sx={{ my: theme.spacing(1) }} />}
          {isExpanded && searchResults && (!loading || searchResults.length > 0) && (
            <KeywordDetail
              searchResults={searchResults}
              reloadData={requestDataFromService}
            />
          )}
        </>
      }
    />
  );
};

export default Keyword;
